"use strict";

import _defineProperty from "@babel/runtime-corejs3/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime-corejs3/helpers/objectWithoutProperties";
var _Stack, _Fragment, _ContainedLayout;
const _excluded = ["children", "client", "optionalAuthentication", "logoUrl"],
  _excluded2 = ["Component", "FallbackComponent"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.promise.js";
import "core-js/modules/es.regexp.to-string.js";
import "core-js/modules/es.weak-map.js";
import "core-js/modules/esnext.weak-map.delete-all.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { invariant } from '@autoguru/utilities';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { NoResults } from '@autoguru/components';
import { BASE_URL } from '@autoguru/global-configs';
import { Button, Inline, Stack, Text } from '@autoguru/overdrive';
import { AsyncBoundary } from '@autoguru/suspense';
import { ContainedLayout } from '../../layout';
import { login, logout } from '../lib/behaviour';
import { cleaClinetBrowserCache, hasAuthParams } from '../lib/utils';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment2 } from "react/jsx-runtime";
const context = /*#__PURE__*/createContext(null);
const handleCallBacks = (client, incomingResolve, incomingReject, failedCount = 0) => {
  const redirectPromiseHandler = (resolve, reject) => {
    client.handleRedirectCallback().then(state => resolve(state)).catch(() => {
      failedCount++;
      if (failedCount > 20) reject('Authentication failed ');else setTimeout(async () => {
        const isAuthed = await client.isAuthenticated();
        if (isAuthed) return resolve({
          isAuthed
        });
        // Try again in 300MS
        handleCallBacks(client, resolve, reject, failedCount);
      }, 500);
    });
  };
  let handleRedirectPromise;
  if (!incomingResolve) handleRedirectPromise = new Promise(redirectPromiseHandler);else redirectPromiseHandler(incomingResolve, incomingReject);
  return handleRedirectPromise;
};
const createAuthResource = (client, optionalAuthentication) => {
  let result = null;
  let error = null;
  const promiseChain = Promise.resolve().then(async () => {
    if (hasAuthParams()) {
      const {
        isAuthed,
        appState
      } = await handleCallBacks(client);
      isAuthed ? await client.checkSession() : window.history.replaceState({}, document.title, (appState === null || appState === void 0 ? void 0 : appState.returnTo) || window.location.toString());
    }
    return client.checkSession();
  }).then(async () => {
    const isAuthenticated = await client.isAuthenticated();
    result = {
      optionalAuthentication,
      isAuthenticated
    };
  }).catch(err => {
    error = err;
  });
  return () => {
    if (result !== null) return result;
    if (error !== null) throw error;
    throw promiseChain;
  };
};
const authClientCache = new WeakMap();
export const AuthProvider = _ref => {
  let {
      children,
      client,
      optionalAuthentication = false,
      logoUrl
    } = _ref,
    incomingOptions = _objectWithoutProperties(_ref, _excluded);
  const defaultOptions = useMemo(() => _objectSpread({
    allow_socials: false,
    logoUrl
  }, incomingOptions), [incomingOptions]);
  const authTracker = useRef(authClientCache.get(client));
  if (!authTracker.current) authClientCache.set(client,
  // biome-ignore lint/suspicious/noAssignInExpressions: migration quick fix. To be fixed when code is touched
  authTracker.current = createAuthResource(client, optionalAuthentication));
  return /*#__PURE__*/_jsx(context.Provider, {
    value: {
      read: authTracker.current,
      options: defaultOptions
    },
    children: children
  });
};

/**
 * Returns some auth related fields, note this will suspend till we know about its value.
 */
export const useAuth = () => {
  const ctx = useContext(context);
  if (process.env.NODE_ENV !== "production") !(ctx !== null) ? process.env.NODE_ENV !== "production" ? invariant(false, "Please define the AuthProvider in this app's tree") : invariant(false) : void 0;
  return _objectSpread(_objectSpread({}, ctx.read()), {}, {
    options: ctx.options
  });
};

/**
 * If the current auth session holds true that the user isnt logged in, then we redirect them to the login flow.
 *
 * Note the returned component will suspend till we know if they're logged in or not.
 */

export const withRequiredAuthentication = (Component, showAuthErrorScreen = false, FallbackComponent, FailedComponent) => props => /*#__PURE__*/_jsx(AsyncBoundary, {
  fallback: null,
  onError: cleaClinetBrowserCache,
  errorFallback: showAuthErrorScreen ? typeof FallbackComponent === 'undefined' ? /*#__PURE__*/_jsx(ContainedLayout, {
    width: "medium",
    children: /*#__PURE__*/_jsx(NoResults, {
      boxed: false,
      title: "Authentication Failed",
      children: /*#__PURE__*/_jsxs(Stack, {
        alignItems: "center",
        space: "3",
        children: [_Stack || (_Stack = /*#__PURE__*/_jsx(Stack, {
          space: "none",
          children: /*#__PURE__*/_jsx(Text, {
            size: "3",
            as: "p",
            align: "center",
            children: "Something went wrong during your authentication"
          })
        })), /*#__PURE__*/_jsxs(Inline, {
          noWrap: true,
          space: "2",
          alignX: "center",
          children: [/*#__PURE__*/_jsx(Button, {
            size: "small",
            variant: "primary",
            onClick: () => login(window.location.toString()),
            children: "Try Again"
          }), /*#__PURE__*/_jsx(Button, {
            size: "small",
            variant: "secondary",
            onClick: () => window.location.assign(BASE_URL),
            children: "Home"
          })]
        })]
      })
    })
  }) : FailedComponent : _Fragment || (_Fragment = /*#__PURE__*/_jsx(_Fragment2, {})),
  children: /*#__PURE__*/_jsx(AuthenticatedComp, _objectSpread(_objectSpread({}, props), {}, {
    Component: Component,
    FallbackComponent: FallbackComponent
  }))
});
//@ts-ignore
const AuthenticatedComp = _ref2 => {
  let {
      Component,
      FallbackComponent
    } = _ref2,
    props = _objectWithoutProperties(_ref2, _excluded2);
  const {
    isAuthenticated,
    optionalAuthentication,
    options
  } = useAuth();
  useEffect(() => {
    if (!isAuthenticated && !optionalAuthentication) {
      login(options.returnTo || window.location.toString(), options);
    }
  }, [options.returnTo, isAuthenticated, optionalAuthentication]);
  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && process.__browser__) {
      // @ts-ignore
      window['logout'] = () => void logout(window.location.toString());
      // @ts-ignore
      window['login'] = () => void login(window.location.toString(), options);
    }
  }, []);
  return isAuthenticated || optionalAuthentication ?
  /*#__PURE__*/
  // @ts-ignore
  _jsx(Component, _objectSpread({}, props)) : typeof FallbackComponent === 'undefined' ? _ContainedLayout || (_ContainedLayout = /*#__PURE__*/_jsx(ContainedLayout, {
    children: /*#__PURE__*/_jsx(Text, {
      align: "center",
      children: "Redirecting..."
    })
  })) : FallbackComponent;
};